import API from 'api';
import { BRAND_DTO_ENUMS } from 'api/repositories/brand/brand.constants';
import { APP_LOCALE, useLanguage } from '@languages/index';
import Button from 'components/button';
import Divider from 'components/divider';
import OverlayBrandTaxInvoiceInfoForm, { OverlayBrandTaxInvoiceInfoFormResult } from 'components/overlayBrandTaxInvoiceInfoForm';
import { isKoreaService } from 'constants/domains/domains';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';
import TextInputField from 'components/textInput';
import PaymentCreate from 'pages/payments/create';
import PaymentComplete from 'pages/payments/complete';

import React, { useEffect, useState } from 'react';
import { X } from 'react-feather';
import { CardElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY);

const ApplyCommissionModal = (props: any) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };

  const [products, setProducts] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [commissionAmount, setCommissionAmount] = useState(
    props.brand?.minCommissionAmount ?? 50000,
  );
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [paymentParams, setPaymentParams] = useState(undefined);
  const isFetchingPaymentParams = React.useRef(true);

  const totalBudget = productCount * commissionAmount;
  const MINIMUM_COMMISSION_AMOUNT = APP_LOCALE === 'ja' ? 3000 : 30000;

  const fetchPaymentParams = async () => {
    const response = await API.Commission.show({
      brandToken: props.auth.token,
      productId: props.product.id,
    });

    setProducts(response.data.products);
    setProductCount(response.data.qty);
    isFetchingPaymentParams.current = false;
  };

  useEffect(() => {
    fetchPaymentParams();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchBrand = async () => {
    const response = await API.Auth.show({ brandToken: props.auth.token });
    props.setBrand(response.data.brand);
    return response.data.brand;
  };

  const handlecommissionAmountChange = (value) => {
    const numericValue = parseInt(value.replace(/[^0-9]/g, '') || 0);
    // if (numericValue >= MINIMUM_COMMISSION_AMOUNT) {
    setCommissionAmount(numericValue);
    // } else {
    // setCommissionAmount(MINIMUM_COMMISSION_AMOUNT)
    // }
  };

  const closeOverlays = () => {
    if (props.preferences.isMobile) {
      props.closeSheet();
    } else {
      props.closePopup();
    }
  };

  const getMinCommissionAmount = () => {
    if (props.brand?.minCommissionAmount) {
      return props.brand.minCommissionAmount;
    }
    return MINIMUM_COMMISSION_AMOUNT;
  };

  return (
    <div
      className="relative bg-white p-[50px] rounded-lg shadow-md max-h-[80vh] overflow-scroll"
      style={{ width: windowWidth < 480 ? `${windowWidth}px` : '480px' }}
    >
      <div className="flex flex-col">
        <div
          className="absolute right-0 top-0 p-[30px] cursor-pointer"
          onClick={() => {
            closeOverlays();
          }}
        >
          <X strokeWidth={1} />
        </div>
        <div className="flex flex-col">
          <h2 className="text-[22px] font-bold mb-[30px]">
            {languageTexts.apply_commission_modal_text_1}
            {'\n'}
            {languageTexts.apply_commission_modal_text_2}
          </h2>
          <div className="text-[15px] mb-[30px]"></div>
          <div className="mb-[30px]">
            <div className="flex items-center mb-[50px]">
              <div className="flex flex-row w-full gap-[8px] items-center">
                <div className="flex flex-col w-full relative gap-[10px] justify-between">
                  <div className="flex flex-col justify-center">
                    <span className="font-bold text-md tracking-tighter">
                      {languageTexts.apply_commission_modal_text_3}
                    </span>
                  </div>

                  <div className="flex flex-col justify-center gap-[8px] text-md tracking-tighter">
                    {products.map((product) => {
                      return (
                        <div
                          key={product.id}
                          data-product-id={product.id}
                          className="flex flex-row justify-between items-start gap-[10px]"
                        >
                          <div
                            className="flex-1 truncate cursor-pointer"
                            title={product.name}
                          >{`${product.name}`}</div>
                          <div className="flex-shrink-0 text-right">
                            {`${product.colorName} / ${product.sizeName} / ${product.qty}${languageTexts.apply_commission_modal_quantity_unit}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center mb-[50px]">
              <div className="flex flex-row w-full gap-[8px] items-center">
                <div className="flex flex-row w-full relative gap-[10px] justify-between">
                  <div className="flex flex-col justify-center">
                    <span className="font-bold text-md tracking-tighter">
                      {languageTexts.apply_commission_modal_text_4}
                    </span>
                  </div>
                  <div className="flex flex-col gap-[15px]">
                    <TextInputField
                      className="flex flex-row w-full h-[48px] max-w-[270px]"
                      bordered
                      textAlign={'right'}
                      placeholder={getMinCommissionAmount()}
                      value={commissionAmount.toLocaleString() ?? 0}
                      onChangeText={(value) => {
                        handlecommissionAmountChange(value);
                      }}
                      unit={languageTexts._apply_commission_modal_text_8(
                        props.settings.currencyText,
                      )}
                    />

                    <div className="flex flex-col justify-center">
                      <span className="text-[#999999] text-sm tracking-tighter">
                        {languageTexts._apply_commission_modal_text_5(
                          props.settings.currencyText,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Divider
              style={{ borderColor: 'rgba(0,0,0,0.1)' }}
              className="my-5"
            />

            <div className="flex flex-row justify-between">
              <span className="font-bold text-md tracking-tighter">
                {'총 결제금액'}
              </span>
              <span className="text-right font-bold text-md tracking-tighter">
                {`${(
                  totalBudget * 1.1
                ).toLocaleString()}${languageTexts._apply_commission_modal_text_8(
                  props.settings.currencyText,
                )}`}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-[15px] w-full">
            <div className="flex flex-col justify-center text-center">
              <span className="text-[#999999] text-sm tracking-tighter text-center">
                {languageTexts.apply_commission_modal_text_6}
              </span>
            </div>

            <Button
              text={languageTexts.apply_commission_modal_text_7}
              textClassName={'font-bold text-md'}
              buttonColor={'black'}
              textColor={'white'}
              onClick={async () => {
                if (isFetchingPaymentParams.current) {
                  return;
                }

                const targetProductType = PAYMENT_PRODUCT_TYPE.SEEDING_COMMISSION;
                const targetAmount = commissionAmount * productCount;

                if (commissionAmount < getMinCommissionAmount()) {
                  props.showModal({
                    zIndex: 1082,
                    text: languageTexts._product_apply_commissions_minimum_amount(
                      getMinCommissionAmount(),
                      props.settings.currencyText,
                    ),
                  });
                  return false;
                }
          
                // 현금 결제 브랜드의 경우 세금계산서 정보 입력
                const currentBrand = await fetchBrand();
                let cashCampaignPaymentData = null
                if (isKoreaService() && currentBrand.billingType === BRAND_DTO_ENUMS.BILLING_TYPE.FREE) {
                  const brandTaxInvoiceInfoFormResult = await new Promise<OverlayBrandTaxInvoiceInfoFormResult>(
                    (resolve, reject) => {
                      props.showOverlays({
                        content: () => (
                          <OverlayBrandTaxInvoiceInfoForm
                            onComplete={(result) => resolve(result)}
                          />
                        )
                      });
                    }
                  );
                  if (!brandTaxInvoiceInfoFormResult.isSubmitted) {
                    return;
                  }
                  cashCampaignPaymentData = brandTaxInvoiceInfoFormResult.data.campaignPayment;
                }

                props.showOverlays({
                  content: () => {
                    return (
                      <Elements stripe={stripePromise}>
                        <PaymentCreate
                          productId={props.product.id}
                          campaignId={props.product.campaignId}
                          itemType={targetProductType}
                          amount={productCount}
                          price={commissionAmount}
                          priceTotal={targetAmount}
                          cashCampaignPaymentData={cashCampaignPaymentData as Pick<
                            OverlayBrandTaxInvoiceInfoFormResult['data']['campaignPayment'], 
                            keyof OverlayBrandTaxInvoiceInfoFormResult['data']['campaignPayment']
                          > | null}
                          onSuccess={async (payment) => {
                            props.onSubmit ? props.onSubmit() : null;

                            setTimeout(() => {
                              props.showOverlays({
                                content: () => {
                                  return (
                                    <PaymentComplete
                                      // title={languageTexts.payment_history2}
                                      itemType={targetProductType}
                                      // amount={productCount}
                                      // price={commissionAmount}
                                      // priceTotal={targetAmount}
                                      onConfirm={() => {
                                        closeOverlays();
                                      }}
                                    />
                                  );
                                },
                              });
                            }, 400);
                          }}
                        />
                      </Elements>
                    );
                  },
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyCommissionModal;
